import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, addDays, subDays, addMonths, isAfter } from "date-fns";
import { CalendarCheck, ChevronDown, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/analytics";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const iso = (d) => format(d, "yyyy-MM-dd");
const ANCHOR = new Date(2026, 9, 9); // First pay day: Fri Oct 9, 2026

function buildPayDays() {
  const list = [];
  const today = new Date();
  const until = addMonths(isAfter(today, ANCHOR) ? today : ANCHOR, 6);
  let d = ANCHOR;
  while (!isAfter(d, until)) {
    list.push(d);
    d = addDays(d, 14);
  }
  return list;
}

export default function PayDayCalendar() {
  const payDays = useMemo(buildPayDays, []);
  const nextIdx = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const i = payDays.findIndex((d) => !isAfter(today, d));
    return i === -1 ? payDays.length - 1 : i;
  }, [payDays]);

  const [openKey, setOpenKey] = useState(null);
  const [cache, setCache] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    if (payDays[nextIdx]) toggle(payDays[nextIdx]);
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggle(d) {
    const key = iso(d);
    if (openKey === key) { setOpenKey(null); return; }
    setOpenKey(key);
    if (!cache[key]) {
      setLoadingKey(key);
      try {
        const { data } = await api.get("/staff/pay-day-preview", { params: { pay_date: key } });
        setCache((c) => ({ ...c, [key]: data }));
      } catch { toast.error("Failed to load pay-day preview."); }
      finally { setLoadingKey(null); }
    }
  }

  return (
    <div data-testid="pay-day-calendar">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><CalendarCheck className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Pay Day Calendar</h1>
          <p className="text-sm text-muted-foreground">Pay days are every other Friday. Each amount = approved hours × hourly rate over the 14 days ending that Friday.</p>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {payDays.map((d, idx) => {
          const key = iso(d);
          const periodStart = subDays(d, 13);
          const isOpen = openKey === key;
          const isNext = idx === nextIdx;
          const data = cache[key];
          return (
            <div key={key} className={`overflow-hidden rounded-2xl border bg-card ${isNext ? "border-primary ring-1 ring-primary/30" : "border-border"}`} data-testid={`payday-card-${key}`}>
              <button type="button" onClick={() => toggle(d)} className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-secondary/40" data-testid={`payday-toggle-${key}`}>
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <div>
                    <p className="font-heading text-lg font-bold">{format(d, "EEE, MMM d, yyyy")}{isNext && <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-white align-middle">Next pay day</span>}</p>
                    <p className="text-xs text-muted-foreground">Pay period: {format(periodStart, "MMM d")} – {format(d, "MMM d, yyyy")} (14 days)</p>
                  </div>
                </div>
                {data && <p className="font-heading text-lg font-extrabold text-primary" data-testid={`payday-total-${key}`}>{money(data.total_pay)}</p>}
              </button>

              {isOpen && (
                <div className="border-t border-border/60 p-4">
                  {loadingKey === key ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
                  ) : !data || data.rows.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground" data-testid={`payday-empty-${key}`}>No approved hours in this pay period.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-border/60">
                      <Table>
                        <TableHeader><TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Amount Owed</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {data.rows.map((r, i) => (
                            <TableRow key={r.user_id || i} data-testid={`payday-row-${key}-${i}`}>
                              <TableCell className="font-medium">{r.worker_name || r.worker_email}</TableCell>
                              <TableCell className="text-right">{r.hours.toFixed(2)}</TableCell>
                              <TableCell className="text-right">{money(r.hourly_rate)}</TableCell>
                              <TableCell className="text-right font-semibold" data-testid={`payday-amount-${key}-${i}`}>{money(r.pay)}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-secondary/40">
                            <TableCell className="font-heading font-bold">Total</TableCell>
                            <TableCell className="text-right font-bold">{data.total_hours.toFixed(2)}</TableCell>
                            <TableCell />
                            <TableCell className="text-right font-bold" data-testid={`payday-grandtotal-${key}`}>{money(data.total_pay)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
